import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuthUser } from 'auth/hooks';
import { useTableActions } from 'core/hooks';
import { jsonToObject, objectToJson } from 'core/utils';
import { ICorporateUserAutocomplete } from 'corporate/types';

import { QuotesDashboardDateRange, QuotesDashboardQuoteType } from './enums';
import { IQuotesDashboardFilterConfig } from './types';

const DEFAULT_FILTERS: Omit<IQuotesDashboardFilterConfig, 'corporateUsers'> = {
  dateRange: QuotesDashboardDateRange.MONTH_TO_DATE,
  quoteType: QuotesDashboardQuoteType.CUSTOMER,
};

const FILTER_ENTITY = 'quotesDashboard';
const FILTER_NAME = 'Default';
const DEBOUNCE_MS = 1000;

export const useQuotesDashboardFilter = () => {
  const { user } = useAuthUser();

  const [filters, setFilters] = useState<IQuotesDashboardFilterConfig>(() => {
    const config = user?.defaultFilters?.[FILTER_ENTITY]?.config;
    const defaultCorporateUsers = user
      ? [
          {
            id: user.corporateUserId,
            name: `${user.firstName} ${user.lastName}`,
            userId: user.id,
          } as ICorporateUserAutocomplete,
        ]
      : [];

    if (!config) {
      return {
        ...DEFAULT_FILTERS,
        corporateUsers: defaultCorporateUsers,
      };
    }

    const parsed = jsonToObject(config) as IQuotesDashboardFilterConfig;

    return {
      corporateUsers:
        parsed.corporateUsers?.length > 0 ? parsed.corporateUsers : defaultCorporateUsers,
      dateRange: parsed.dateRange || DEFAULT_FILTERS.dateRange,
      quoteType: parsed.quoteType || DEFAULT_FILTERS.quoteType,
    };
  });
  const [isDirty, setIsDirty] = useState(false);

  const { upsertCustomFilter } = useTableActions({ silent: true });

  const existingFilterId = user?.defaultFilters?.[FILTER_ENTITY]?.id;

  const debouncedSave = useMemo(
    () =>
      debounce((newFilters: IQuotesDashboardFilterConfig, filterId?: number) => {
        upsertCustomFilter({
          id: filterId,
          entity: FILTER_ENTITY,
          name: FILTER_NAME,
          config: objectToJson(newFilters),
          default: 1,
        });
      }, DEBOUNCE_MS),
    [upsertCustomFilter]
  );

  useEffect(() => {
    if (!isDirty) {
      return;
    }

    debouncedSave(filters, existingFilterId);
  }, [filters, existingFilterId, isDirty, debouncedSave]);

  const setCorporateUsers = useCallback((corporateUsers: ICorporateUserAutocomplete[]) => {
    setFilters((prev) => ({ ...prev, corporateUsers }));
    setIsDirty(true);
  }, []);

  const setDateRange = useCallback((dateRange: QuotesDashboardDateRange) => {
    setFilters((prev) => ({ ...prev, dateRange }));
    setIsDirty(true);
  }, []);

  const setQuoteType = useCallback((quoteType: QuotesDashboardQuoteType) => {
    setFilters((prev) => ({ ...prev, quoteType }));
    setIsDirty(true);
  }, []);

  return {
    filters,
    setCorporateUsers,
    setDateRange,
    setQuoteType,
  };
};
